import type { TOC } from '@ember/component/template-only';
import {
  PrismBanner,
  PrismButton,
  PrismLoadingBar,
} from '@mylighthouse/prism-ember';
import { ICONS_LOGOS_MAP } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { FocusScreen, StepIndicator } from 'frontend-utils';
import HotelWizardsPreferredRateOnboardingPreferredRateOnboardingOptions from 'lumen/components/hotel/wizards/preferred-rate-onboarding/preferred-rate-onboarding-options';
import type HotelWizardsPreferredRateOnboardingRateTypeController from 'lumen/controllers/hotel/wizards/preferred-rate-onboarding/rate-type';
import { PREFERRED_RATE_ONBOARDING_TOTAL_STEPS } from 'lumen/utils/constants/preferred-rate-onboarding';

interface HotelWizardsPreferredRateOnboardingRateTypeSignature {
  Args: {
    controller: HotelWizardsPreferredRateOnboardingRateTypeController;
    model: unknown;
  };
}

<template>
  <FocusScreen
    @hasCancel={{false}}
    @isCancelDisabled={{true}}
    @logoHtml={{ICONS_LOGOS_MAP.logo-dark-with-text}}
  >
    <:header>
      <div class="wizards-preferred-rate-onboarding__header">
        <h3>{{t "wizards.preferred_rate_onboarding.header"}}</h3>
        <div class="wizards-preferred-rate-onboarding__header__step-indicator">
          <StepIndicator
            @currentStep={{1}}
            @steps={{PREFERRED_RATE_ONBOARDING_TOTAL_STEPS}}
          />
        </div>
      </div>
    </:header>

    <:body>
      <div class="wizards-preferred-rate-onboarding__body">
        {{#if
          @controller.model.requestPricingAssistantRatePreferencesOptionsTaskResult.isRunning
        }}
          <PrismLoadingBar />
        {{else}}
          <div class="wizards-preferred-rate-onboarding__content">
            <h1 class="mb-400">
              {{t "wizards.preferred_rate_onboarding.rate_type.content.title"}}
            </h1>

            <h4 class="text-300--regular mb-600">
              {{t
                "wizards.preferred_rate_onboarding.rate_type.content.sub_title"
              }}
            </h4>

            {{#unless
              @controller.model.requestPricingAssistantRatePreferencesOptionsTaskResult.isRunning
            }}
              <HotelWizardsPreferredRateOnboardingPreferredRateOnboardingOptions
                @options={{@controller.rateTypeOptions}}
                @updateOption={{@controller.updateSelectedRateType}}
              />
            {{/unless}}

            <div class="wizards-preferred-rate-onboarding__content--banner">
              <PrismBanner
                @bannerMessage={{t
                  "wizards.preferred_rate_onboarding.rate_type.banner.message"
                }}
                @bannerStyle="info"
                @bannerTitle={{t
                  "wizards.preferred_rate_onboarding.rate_type.banner.title"
                }}
                @icon="circle-information-outline"
              />
            </div>
          </div>
        {{/if}}
      </div>
    </:body>

    <:footer>
      <div class="wizards-preferred-rate-onboarding__footer">
        <div class="flex gap-300">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.user_details_skip_button"}}
            @onClick={{@controller.onSkip}}
            @size="large"
          />

          <PrismButton
            @buttonType="brand"
            @iconAfter="arrow-right"
            @isDisabled={{@controller.model.requestPricingAssistantRatePreferencesOptionsTaskResult.isRunning}}
            @label={{t "general.continue"}}
            @onClick={{@controller.onContinue}}
            @size="large"
            data-test-id="wizards-preferred-rate-onboarding__continue-button"
          />
        </div>
      </div>
    </:footer>
  </FocusScreen>
</template> satisfies TOC<HotelWizardsPreferredRateOnboardingRateTypeSignature>;
